import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "../../lib/api";

const PERIODS = [
  { v: "30", l: "30 giorni" },
  { v: "90", l: "90 giorni" },
  { v: "365", l: "12 mesi" },
  { v: "all", l: "Tutto l'archivio" },
  { v: "custom", l: "Personalizzato" },
];

function fmtPct(rate) {
  if (rate === null || rate === undefined) return "Dati insufficienti";
  return `${Math.round(rate * 1000) / 10}%`;
}

function Section({ title, testid, children, caption }) {
  return (
    <section className="min-w-0 rounded-lg border border-[#2a2926] bg-[#161514] p-5 sm:p-6" data-testid={testid}>
      <h3 className="mb-1 text-[9px] font-bold tracking-[0.22em] text-gold">{title}</h3>
      {caption && <p className="mb-4 text-[10px] italic leading-relaxed text-[#777]">{caption}</p>}
      <div className={caption ? "" : "mt-4"}>{children}</div>
    </section>
  );
}

function Cards({ p }) {
  const items = [
    { k: "contatti_unici", l: "Contatti unici" },
    { k: "richieste", l: "Richieste" },
    { k: "prenotazioni", l: "Prenotazioni" },
    { k: "attivazioni_registrate_periodo", l: "Protocolli attivati" },
    { k: "opportunita_aperte_oggi", l: "Opportunità in corso (oggi)" },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="cd-cards">
      {items.map((i) => (
        <div key={i.k} className="rounded-lg border border-[#2a2926] bg-[#161514] p-4" data-testid={`cd-card-${i.k}`}>
          <p className="font-mono text-2xl text-gold-light" data-testid={`cd-value-${i.k}`}>{p[i.k]}</p>
          <p className="mt-1 text-[10px] tracking-wide text-[#777]">{i.l}</p>
        </div>
      ))}
    </div>
  );
}

function Funnel({ rows, exits }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div data-testid="cd-funnel">
      <div className="grid gap-2.5">
        {rows.map((r) => (
          <div key={r.key} className="grid items-center gap-2" style={{ gridTemplateColumns: "minmax(120px,170px) 1fr auto" }} data-testid={`cd-funnel-row-${r.key}`}>
            <span className="truncate text-[11px] text-[#c9c6bf]" title={r.label}>{r.label}</span>
            <div className="h-1.5 bg-[#242321]">
              <div className="h-full bg-gold transition-all duration-500" style={{ width: `${Math.round((r.count / max) * 100)}%` }} />
            </div>
            <span className="w-28 text-right font-mono text-[11px] text-gold-light">
              {r.count} <span className="text-[9px] text-[#777]">· {fmtPct(r.rate_next)}</span>
            </span>
          </div>
        ))}
      </div>
      <p className="mt-4 text-[10px] text-[#777]" data-testid="cd-funnel-exits">
        Uscite come "Non acquisito": <span className="font-mono text-gold-light">{exits}</span>
      </p>
    </div>
  );
}

function RowsTable({ rows, cols, testid, empty }) {
  if (!rows.length) return <p className="text-[12px] text-[#777]" data-testid={`${testid}-empty`}>{empty}</p>;
  return (
    <div className="w-full overflow-x-auto" data-testid={testid}>
      <table className="w-full min-w-[420px] border-collapse text-[11px]">
        <thead>
          <tr className="border-b border-[#2a2926] text-left text-[9px] tracking-[0.18em] text-[#777]">
            {cols.map((c) => <th key={c.k} className="py-2 pr-3 font-bold">{c.l}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.key || r.nome || i} className="border-b border-[#242321] last:border-0">
              {cols.map((c, j) => (
                <td key={c.k} className={`py-2.5 pr-3 ${j === 0 ? "text-[#c9c6bf]" : "font-mono text-gold-light"}`}>
                  {c.k === "conversione" ? fmtPct(r[c.k]) : (r[c.k] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function CommercialDashboard() {
  const [period, setPeriod] = useState("90");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [protocol, setProtocol] = useState("tutti");
  const [source, setSource] = useState("tutte");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      const params = { protocol, source };
      if (period === "custom") {
        if (from) params.from_d = from;
        if (to) params.to_d = to;
      } else if (period !== "all") {
        const d = new Date();
        d.setDate(d.getDate() - Number(period));
        params.from_d = d.toISOString().slice(0, 10);
      }
      const { data: d } = await api.get("/admin/commercial-dashboard", { params });
      setData(d);
    } catch {
      setError("Impossibile caricare la dashboard. Riprova.");
      setData(null);
    } finally {
      setBusy(false);
    }
  }, [period, from, to, protocol, source]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="mb-10" data-testid="commercial-dashboard">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-sm font-bold tracking-[0.2em] text-gold">DASHBOARD COMMERCIALE</h2>
          <p className="mt-1 text-[11px] text-[#777]">Percorso fino all'attivazione del protocollo · dati DEMO sempre esclusi</p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="cd-filters">
          <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="cd-filter-period"
            className="rounded border border-[#2a2926] bg-[#161514] px-2 py-1.5 text-[11px] text-[#c9c6bf]">
            {PERIODS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
          </select>
          <select value={protocol} onChange={(e) => setProtocol(e.target.value)} data-testid="cd-filter-protocol"
            className="rounded border border-[#2a2926] bg-[#161514] px-2 py-1.5 text-[11px] text-[#c9c6bf]">
            {["tutti", "GENERAL", "AC_START", "AC_HUNT", "AC_SELECT", "AC_INVEST", "AC_MANAGEMENT"].map((v) => (
              <option key={v} value={v}>{v === "tutti" ? "Tutti i protocolli" : v.replace("AC_", "AC ")}</option>
            ))}
          </select>
          <select value={source} onChange={(e) => setSource(e.target.value)} data-testid="cd-filter-source"
            className="rounded border border-[#2a2926] bg-[#161514] px-2 py-1.5 text-[11px] text-[#c9c6bf]">
            {["tutte", "hero", "ac-start", "ac-hunt", "ac-select", "ac-invest", "ac-management", "altro"].map((v) => (
              <option key={v} value={v}>{v === "tutte" ? "Tutte le fonti" : v === "hero" ? "Homepage" : v.toUpperCase()}</option>
            ))}
          </select>
          {period === "custom" && (
            <span className="flex items-center gap-2">
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="cd-filter-from"
                className="rounded border border-[#2a2926] bg-[#161514] px-2 py-1.5 text-[11px] text-[#c9c6bf]" />
              <span className="text-[#777]">→</span>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="cd-filter-to"
                className="rounded border border-[#2a2926] bg-[#161514] px-2 py-1.5 text-[11px] text-[#c9c6bf]" />
            </span>
          )}
          <button onClick={load} disabled={busy} data-testid="cd-refresh-button"
            className="rounded border border-[#3a3833] px-3 py-1.5 text-[10px] font-bold tracking-[0.15em] text-gold transition-colors hover:bg-[#242321] disabled:opacity-40">
            {busy ? <Loader2 size={12} className="animate-spin" /> : "APPLICA"}
          </button>
        </div>
      </div>

      {error && <p className="rounded border border-red-900/50 bg-red-950/30 p-3 text-[11px] text-red-300" data-testid="cd-error">{error}</p>}

      {data && (
        <div className="grid gap-5">
          <Cards p={data.panoramica} />
          <div className="flex flex-wrap items-center gap-3 text-[10px]" data-testid="cd-untracked-note">
            {data.panoramica.richieste_senza_tracciamento > 0 ? (
              <span className="rounded border border-[#4a4020] bg-[#2a2416] px-2 py-1 text-[#d8b45a]"
                data-testid="cd-untracked-badge">
                {data.panoramica.richieste_senza_tracciamento} richiesta/e del periodo senza tracciamento di sessione (storico precedente al 27/10/2026) — non ricostruite
              </span>
            ) : (
              <span className="text-[#777]">Tutte le richieste del periodo hanno il tracciamento di sessione.</span>
            )}
          </div>
          <Section title="FUNNEL COMMERCIALE" testid="cd-funnel-section" caption={`${data.note.funnel} Il tasso accanto a ogni fase è la progressione verso la fase successiva: 0% significa che nessun contatto è ancora passato oltre.`}>
            <Funnel rows={data.funnel} exits={data.uscite_non_acquisito} />
          </Section>
          <div className="grid gap-5 lg:grid-cols-2">
            <Section title="PER PROTOCOLLO" testid="cd-protocol-section" caption={data.note.attivazioni}>
              <RowsTable testid="cd-protocol-table" empty="Nessun contatto nel periodo."
                rows={data.protocolli.filter((p) => p.contatti > 0 || p.richieste > 0)}
                cols={[{ k: "label", l: "Protocollo" }, { k: "contatti", l: "Contatti" }, { k: "richieste", l: "Richieste" }, { k: "prenotazioni", l: "Prenotazioni" }, { k: "attivati", l: "Attivati" }, { k: "conversione", l: "Attivazione" }]} />
            </Section>
            <Section title="PROVENIENZE (prima provenienza)" testid="cd-provenance-section">
              <RowsTable testid="cd-provenance-table" empty="Nessun contatto nel periodo."
                rows={data.provenienze}
                cols={[{ k: "label", l: "Fonte" }, { k: "contatti", l: "Contatti" }, { k: "richieste", l: "Richieste" }, { k: "attivati", l: "Attivati" }, { k: "conversione", l: "Attivazione" }]} />
            </Section>
          </div>
          <Section title="CAMPAGNE (dalle richieste)" testid="cd-campaign-section"
            caption="La campagna è quella registrata sulla singola richiesta; disponibile solo per le richieste recenti.">
            <RowsTable testid="cd-campaign-table" empty="Nessuna campagna registrata nel periodo."
              rows={data.campagne}
              cols={[{ k: "nome", l: "Campagna" }, { k: "medio", l: "Mezzo" }, { k: "richieste", l: "Richieste" }]} />
          </Section>
          <p className="text-[10px] leading-relaxed text-[#666]" data-testid="cd-note-storico">{data.note.storico}</p>
        </div>
      )}

      {!data && busy && (
        <div className="flex justify-center py-16" data-testid="cd-loading"><Loader2 size={22} className="animate-spin text-gold" /></div>
      )}
    </div>
  );
}
